import React, { useEffect, FC, useState, useRef } from 'react';
import FocusTrap from 'focus-trap-react';
import { motion, AnimatePresence } from 'framer-motion';

import { Loading } from 'components/atoms';
import { ScrollableDialogOverlayProps, ScrollableModalProps } from './types';
import {
  ConfirmButton,
  DeclineButton,
  ScrollableDialogBody,
  ScrollableDialogClose,
  ScrollableDialogComponent,
  ScrollableDialogFooter,
  ScrollableDialogHeading,
  ScrollableDialogOverlayComponent,
  ScrollableDialogTitle,
} from './TermsAndConditions.styles';

const ScrollableDialogOverlay: FC<ScrollableDialogOverlayProps> = ({
  children,
  onDecline,
  onConfirm,
  title,
  confirmButton = 'Confirm',
  declineButton = 'Decline',
  loading = false,
}) => {
  const [isConfirmEnabled, setIsConfirmEnabled] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkScrollPosition = () => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const { scrollTop, scrollHeight, clientHeight } = contentElement;

    if (scrollTop + clientHeight >= scrollHeight - 5) {
      setIsConfirmEnabled(true);
    }
  };

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleScroll = () => checkScrollPosition();
    contentElement.addEventListener('scroll', handleScroll);

    checkScrollPosition();

    return () => contentElement.removeEventListener('scroll', handleScroll);
  }, [children, loading]);

  useEffect(() => {
    if (!onDecline) {
      return;
    }
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        onDecline();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onDecline]);

  return (
    <ScrollableDialogOverlayComponent>
      <ScrollableDialogHeading className="Dialog">
        {title && <ScrollableDialogTitle>{title}</ScrollableDialogTitle>}
        <ScrollableDialogClose aria-label="Close">
          <img src="logo-regular.png" alt="Control Risks" width="230" height="25" />
        </ScrollableDialogClose>
      </ScrollableDialogHeading>
      <ScrollableDialogBody ref={contentRef}>{children}</ScrollableDialogBody>
      <ScrollableDialogFooter>
        <DeclineButton type="button" disabled={loading} onClick={onDecline}>
          {declineButton}
        </DeclineButton>

        <ConfirmButton
          type="button"
          onClick={onConfirm}
          disabled={!isConfirmEnabled || loading}
          loading={loading}
        >
          {loading ? <Loading /> : confirmButton}
        </ConfirmButton>
      </ScrollableDialogFooter>
    </ScrollableDialogOverlayComponent>
  );
};

const ScrollableDialog: FC<ScrollableModalProps> = ({
  children,
  isOpen = false,
  onDecline,
  onConfirm,
  title,
  confirmButton,
  declineButton,
  loading,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <FocusTrap>
            <ScrollableDialogComponent>
              <ScrollableDialogOverlay
                title={title}
                onDecline={onDecline}
                onConfirm={onConfirm}
                confirmButton={confirmButton}
                declineButton={declineButton}
                loading={loading}
              >
                {children}
              </ScrollableDialogOverlay>
            </ScrollableDialogComponent>
          </FocusTrap>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ScrollableDialog;
